<template>
    <Accordion v-if="states.length" class="pl-2 pr-2 space-y-2 bg-opacity-30 bg-slate-400" title="States"
        :modified="statesModified">
        <Accordion v-for="stateName in states" :key="stateName"
            class="pl-2 pr-2 space-y-1 bg-opacity-20 bg-slate-500"
            :title="stateLabel(stateName)"
            :modified="isStateModified(stateName)">
            <div class="flex justify-end">
                <button type="button"
                    class="text-xs px-2 py-0.5 rounded"
                    :class="isPreviewing(stateName) ? 'bg-green-600 hover:bg-green-500 text-white' : 'bg-slate-600 hover:bg-slate-500 text-slate-200'"
                    :title="isPreviewing(stateName) ? 'Stop previewing this state on canvas' : 'Preview this state on canvas'"
                    @click="togglePreview(stateName)">
                    {{ isPreviewing(stateName) ? 'Stop Test' : 'Test' }}
                </button>
            </div>
            <div v-for="partName in partNames" :key="partName" class="space-y-1">
                <div class="text-xs font-semibold text-slate-300">{{ partLabelFor(partName) }}</div>
                <PartPropertiesPanel :key="editorKey(`${stateName}:${partName}`)"
                    :node="node" :partName="partName" :state="stateName" />
            </div>
        </Accordion>
    </Accordion>
</template>

<script>
import Accordion from '../Accordion.vue';
import PartPropertiesPanel from './PartPropertiesPanel.vue';
import { STATE_LABELS } from '../../Hasp/properties/v2/stateFormat.js';
import { partLabel } from './partLabels.js';
import { stateGroupTree, isGroupModified } from '../../Hasp/properties/propertyGroupStatus.js';
import { styleRefreshMixin } from './styleRefreshMixin.js';
import { propertyEditorKey } from './propertyEditorKey.js';

// Dedicated "States" accordion — restores the pre-refactor workflow of
// editing state overrides in one place, separate from the default-appearance
// Part Properties panels. Purely a presentation layer: every read/write goes
// through PartPropertiesPanel (unmodified) and the existing state-aware node
// API (stateStyleProp/setStateStyle/hasExplicitStyle/resetStateStyle) — no
// state-specific logic lives here beyond iterating what the node reports via
// getSupportedStates()/getPartNames(), so a future LVGL state needs no
// changes here, only a data-table update (see stateFormat.js).
export default {
    name: 'StatesPanel',
    components: { Accordion, PartPropertiesPanel },
    // Style writes bypass Vue reactivity, so the modified indicators below need
    // the node's own style-refresh events to re-render (see the mixin).
    mixins: [styleRefreshMixin],

    props: {
        node: { type: Object, required: true },
    },

    data() {
        return { previewingState: null };
    },

    computed: {
        states() {
            return this.node?.getSupportedStates() ?? [];
        },
        // One group per supported state plus the folded root — both indicators
        // come from the same tree, so a deeper nesting later needs no new logic
        // (see propertyGroupStatus.js). Reading styleTick registers this as a
        // render dependency on any style write.
        stateGroups() {
            void this.styleTick;
            return stateGroupTree(this.node);
        },
        statesModified() {
            return isGroupModified(this.stateGroups);
        },
        // Only parts that actually have a user-visible (non-internal)
        // property are worth a sub-section here.
        partNames() {
            return (this.node?.getPartNames() ?? []).filter(partName =>
                Object.values(this.node.getStyleDefinition(partName)?.properties ?? {})
                    .some(meta => !meta?.internal));
        },
    },

    watch: {
        // Selecting a different node while previewing must not leave the
        // PREVIOUS node stuck in its preview state.
        node(newNode, oldNode) {
            if (this.previewingState && oldNode) oldNode.clearPreviewState();
            this.previewingState = null;
        },
    },

    beforeUnmount() {
        if (this.previewingState) this.node.clearPreviewState();
    },

    methods: {
        editorKey(context) {
            return propertyEditorKey(this.node, `state:${context}`);
        },
        stateLabel(stateName) {
            return STATE_LABELS[stateName] ?? stateName;
        },
        // Per-state indicator — read from the same tree the root folds.
        isStateModified(stateName) {
            return this.stateGroups.children.some(child => child.id === stateName && child.modified);
        },
        partLabelFor(partName) {
            return partLabel(this.node, partName);
        },
        isPreviewing(stateName) {
            return this.previewingState === stateName;
        },
        // Preview is a thin UI layer over the existing StyleResolver cascade
        // (see ObjectGroup.setPreviewState/clearPreviewState) — it never
        // writes stored data, creates history entries, or affects export; it
        // only switches which state style()/styleProp() resolve to when a
        // widget's own redraw code omits the state argument, then generically
        // re-fires every style-refresh event so the canvas updates immediately.
        togglePreview(stateName) {
            if (this.previewingState === stateName) {
                this.node.clearPreviewState();
                this.previewingState = null;
            } else {
                this.node.setPreviewState(stateName);
                this.previewingState = stateName;
            }
        },
    },
};
</script>
